/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useEffect, useMemo } from 'react';
// material
import { useDispatch, useSelector } from 'react-redux';
import { Button, displayModal } from '@insticator/insticator-ui';
import Space from 'material/space/space';
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ConfigV2Controller } from 'material/expandablesection/v2';
import { ReactComponent as BoardActivity } from 'assets/icons/board-activity.svg'
// content
import ProductGroup from './productgroup/productgroup'
// styles
import injectSheet from 'react-jss'
import styles from './pagesetupv2_styles'
import { setTempProductManagerConfig } from 'state/slices/productmanager';
import ProductGroupV2 from './productgroupv2/productgroupv2';
import AddProductGroupModal from './addproductgroupmodal/addproductgroupmodal';
import { Dialog } from '@mui/material';


/* ========== ~~~~~~~~~~ PRODUCT MANAGER PAGE: PAGE SETUP ~~~~~~~~~~ ========== */
const PageSetupV2 = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const { tempProductManagerConfig, productManagerConfig, productManagerConfigChanges }  = useSelector(state => state.productManager)
  const {
    account: {
      accountProducts: approvedAccountProducts
    }
  } = useSelector((state) => state)

  const { sites } = useSelector((state) => state.account);
  const { siteUUID } = useSelector(state => state.session)

  const [isOpened, setIsOpened] = useState(false)
 
  const handleToggle = () => setIsOpened(!isOpened)

  const [isAddProductGroupModalOpen, openAddProductGroupModal] = useState(false)

  const handleAddGroup = useCallback((e) => {
    e.stopPropagation()
    openAddProductGroupModal(true)
    // dispatch(displayModal(true, <AddProductGroupModal /> ))
  }, [])

  // const isProductGroupEdited = productManagerConfigChanges.includes('groupConfigList') && tempProductManagerConfig.groupConfigList.length > 0

  const isProductGroupEdited = !(JSON.stringify(tempProductManagerConfig.groupConfigList) === JSON.stringify(productManagerConfig.groupConfigList));

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      actionButtons={
        <Button 
          name={'creategroup'} 
          type={'action'}
          variant={'none'} 
          color={'light'} 
          disabled={tempProductManagerConfig.groupConfigList.length > 1 && isProductGroupEdited}
          text={'Add Product Group'} 
          handleClick={handleAddGroup} 
        />
      }
      handleToggle={handleToggle}
      icon={<BoardActivity />}
      title='Page Setup'
    />
  );

  const handleProductGroupChange = useCallback((index, property, value, selectedMode = 'draft') => {
    const updatedGroups = JSON.parse(JSON.stringify(tempProductManagerConfig.groupConfigList))
    updatedGroups[index][selectedMode] = {...updatedGroups[index][selectedMode], [property]: value}
    // Set to hardcode when infinite scroll is enabled
    if(property === 'infiniteScrollEnabled' && value === true) {
      updatedGroups[index][selectedMode]['dynamicPlacement'] = false 
    }

    if(property === 'infiniteScrollEnabled' && value === false) {
      updatedGroups[index][selectedMode]['dynamicPlacement'] = false 
    }

    if(property === 'label') {
      updatedGroups[index]['saveDraftText'] = 'Save Draft'
    } else {
      updatedGroups[index]['saveDraftText'] = 'Save for Testing'
    }

    // if there's no product selected disable group 
    // TODO: V2 For article sentiment
    if(updatedGroups[index][selectedMode]['showCommenting'] === false &&
       updatedGroups[index][selectedMode]['showTrendingNow'] === false && 
       updatedGroups[index][selectedMode]['showArticleSentiment'] === false && 
       property !== 'groupEnabled') {
      updatedGroups[index][selectedMode]['groupEnabled'] = false 
    }

    if(property === 'groupEnabled' && value === true) {
      updatedGroups[index][selectedMode]['showCommenting'] = true
      updatedGroups[index][selectedMode]['showTrendingNow'] = true
      updatedGroups[index][selectedMode]['showArticleSentiment'] = true
    } 

    const newConfig = {...tempProductManagerConfig, groupConfigList: updatedGroups}
    dispatch(setTempProductManagerConfig({config: newConfig, changes: property === 'label' ? 'label' : 'groupConfigList'}))
  }, [dispatch, tempProductManagerConfig])

  const handleProductGroupPropertiesChange = useCallback((index, newPropertyValues, selectedMode = 'draft') => {
    // For multiple properties change at once
    const updatedGroups = JSON.parse(JSON.stringify(tempProductManagerConfig.groupConfigList))
    updatedGroups[index][selectedMode] = {...updatedGroups[index][selectedMode],...newPropertyValues}
    if(updatedGroups[index][selectedMode]['showCommenting'] === false &&
       updatedGroups[index][selectedMode]['showTrendingNow'] === false &&
       updatedGroups[index][selectedMode]['showArticleSentiment'] === false ) {
      updatedGroups[index][selectedMode]['groupEnabled'] = false 
    }

    let newConfig = {...tempProductManagerConfig, groupConfigList: updatedGroups}

    // if changes is control bar config apply it
    if(Object.keys(newPropertyValues).includes('controlBarConfig')) {
      newConfig = {
        isControlBarChanged: true,
        ...tempProductManagerConfig, 
        controlBarConfig: {
          ...tempProductManagerConfig.controlBarConfig,
          draft: {
            ...tempProductManagerConfig.controlBarConfig?.draft || undefined,
            ...newPropertyValues.controlBarConfig
          }
        },
        groupConfigList: updatedGroups
      }
    }

    dispatch(setTempProductManagerConfig({config: newConfig, changes: 'groupConfigList'}))
  }, [dispatch, tempProductManagerConfig])

  const handleProductGroupRemove = useCallback((index) => {
    const updatedGroups = tempProductManagerConfig.groupConfigList.filter((group, i) => i !== index)
    const removedId = tempProductManagerConfig.groupConfigList[index].groupId
    
    // if existing group, add it on the removedGroupIds array
    let removedGroupIds = Array.isArray(tempProductManagerConfig.removedGroupIds) ? [...tempProductManagerConfig.removedGroupIds] : []
    if(removedId) removedGroupIds = [...removedGroupIds, removedId]
  
    dispatch(setTempProductManagerConfig({config: {...tempProductManagerConfig, groupConfigList: updatedGroups, removedGroupIds }, changes: 'groupConfigList'}))
  },[dispatch, tempProductManagerConfig])

  const renderGroup = () => {
    const isGroupEmpty = tempProductManagerConfig.groupConfigList.length === 0
    return !isGroupEmpty ? tempProductManagerConfig.groupConfigList.map((group, index) => {
    const savedDraft = productManagerConfig.groupConfigList.find(grp => grp.groupId === group.groupId)?.draft
    const isDraftChanged = !(JSON.stringify(group.draft) === JSON.stringify(savedDraft));
      return (
        <ProductGroupV2 
          index={index}
          group={group}
          controlBarConfig={tempProductManagerConfig.controlBarConfig}
          siteURL={sites[siteUUID] || ''}
          onRemove={() => handleProductGroupRemove(index)} 
          onChange={(property, value, selectedMode) => {
            handleProductGroupChange(index, property, value, selectedMode)
          }} 
          onFieldsChange={(properties, selectedMode) => {
            handleProductGroupPropertiesChange(index, properties, selectedMode)
          }} 
          isDraftChanged={isDraftChanged}
          saveDraftText={group.saveDraftText || 'Save for Testing'}
        />
      )
    }) : <Space alignItems={'center'} justifyContent={'center'} style={{height: '167px'}}>No product groups</Space>
  }

  return (
    <ExpandableSection
      className={`expandablesectionv2 pagesetupv2 ${props.classes.pagesetupv2}`}
      controller={controller}
      isOpened={isOpened}
      disableSave={false}
    >
      <Space gap={16} direction={'column'}>
        {isAddProductGroupModalOpen && (
          <Dialog classes={'addmodal'} maxWidth={'xl'} open={isAddProductGroupModalOpen}>
            <AddProductGroupModal 
              onSave={() => openAddProductGroupModal(false)}
              onCancel={() => openAddProductGroupModal(false)} 
            />
          </Dialog>
        )}
        {renderGroup()}
      </Space>
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default PageSetupV2;